import { getAuthHeaders } from '@/hooks/useAuth';

const headers = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' });

// What a member sees about their own referral link. Every paid Plus member has one
// (minted automatically); `isAffiliate: false` now means "not a paid member".
export interface AffiliateMe {
  isAffiliate: boolean;
  code?: string;
  name?: string;
  active?: boolean;
  ratePercent?: number;
  link?: string;          // plus.heyjoe.io/?ref=code — the general-purpose link
  charterLink?: string;   // plus.heyjoe.io/charter?ref=code
  holdDays?: number;
  connectOnboarded?: boolean;
  payoutMethod?: 'wallet' | 'cash';
  eventAlerts?: boolean;   // email me my link ~3 days before each live event
  walletCents?: number;
  sales?: number;
  grossCents?: number;
  pendingCents?: number;
  heldCents?: number;
  paidCents?: number;
  voidedCents?: number;
  eventLinks?: { productId: string; title: string; eventDate: string; link: string }[];   // short /w/ link per upcoming event
}

export interface AffiliateConnectStatus {
  connected: boolean;
  onboarded: boolean;
  accountId?: string;
  source?: string | null;
}

export interface WalletTransaction {
  id: string;
  type: string;
  amountCents: number;
  balanceAfter: number | null;
  createdAt: string;
  note?: string | null;
  productTitle?: string | null;
  sales?: number | null;
}

export interface Wallet {
  balanceCents: number;
  /** The part of the balance that was EARNED and can be moved to a bank account.
   *  Admin grants and credit refunds are spendable but never cashable. */
  cashableCents?: number;
  cashOutMinCents?: number;
  payoutOnboarded?: boolean;
  /** Promotional thank-you credit still live: what's left of it and when it ends. */
  bonuses?: { amountCents: number; remainingCents: number; expiresAt: string | null; offer: string | null }[];
  transactions: WalletTransaction[];
}

export async function fetchAffiliateMe(): Promise<AffiliateMe> {
  const res = await fetch('/api/affiliate/me', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Could not load your referral details.');
  return res.json();
}

export async function fetchWallet(): Promise<Wallet> {
  const res = await fetch('/api/wallet', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Could not load your Hey Joe credit.');
  return res.json();
}

export async function cashOutWallet(cents?: number): Promise<{ cents: number; balanceCents: number; cashableCents: number }> {
  const res = await fetch('/api/wallet/cash-out', {
    method: 'POST', headers: headers(),
    body: JSON.stringify(cents === undefined ? { all: true } : { cents }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Could not cash out.'); }
  return res.json();
}

export async function setAffiliatePayoutMethod(method: 'wallet' | 'cash'): Promise<void> {
  const res = await fetch('/api/affiliate/payout-method', { method: 'POST', headers: headers(), body: JSON.stringify({ method }) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Could not update how you get paid.'); }
}

export async function setEventAlerts(enabled: boolean): Promise<void> {
  const res = await fetch('/api/affiliate/alerts', { method: 'POST', headers: headers(), body: JSON.stringify({ enabled }) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Could not update event alerts.'); }
}

export async function fetchAffiliateConnectStatus(): Promise<AffiliateConnectStatus> {
  const res = await fetch('/api/affiliate/connect/status', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Could not check your payout status.');
  return res.json();
}

export async function startAffiliateOnboarding(): Promise<string> {
  const res = await fetch('/api/affiliate/connect/onboard', { method: 'POST', headers: headers() });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Could not start payout setup.'); }
  const data = await res.json();
  return data.url;
}

// Build the share link for a specific event/course page. The ref rides on the
// course URL so the friend lands on the thing being shared, not the homepage.
export function referralLinkFor(code: string, productId?: string | null): string {
  const base = 'https://plus.heyjoe.io';
  return productId
    ? `${base}/course/${productId}?ref=${encodeURIComponent(code)}`
    : `${base}/?ref=${encodeURIComponent(code)}`;
}

// Shareable links for the account page: upcoming events, each with the member's own
// short referral link, and the membership. No homepage link: it gives a friend
// nothing to act on (Joe, Oct 16 2026).
export interface ReferralEventLink {
  productId: string;
  title: string;
  date: string;
  coverImage: string | null;
  priceCents: number;
  freeForMembers: boolean;
  membersOnly: boolean;
  link: string;
}
export interface ReferralLinks {
  code: string | null;
  events: ReferralEventLink[];
  membership: { title: string; link: string } | null;
}
export async function fetchReferralLinks(): Promise<ReferralLinks> {
  const res = await fetch('/api/affiliate/me/links', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Could not load your links.');
  return res.json();
}
